import { sentry } from '@frontend/tracking';

/**
 * Helper function that logs an error to the console and also in Sentry.
 */
export const logError = (message: string, fnName: string, context: Record<string, any>) => {
  console.error(message);
  sentry.error({
    error: message,
    topic: 'phone-config',
    team: 'fe-phone-config',
    severityLevel: 'error',
    addContext: {
      name: fnName,
      context,
    },
  });
};

/**
 * Recursively iterates over the properties of an object and executes a callback function for each property.
 * NOTE: if the property is of type object, the callback function is executed for each property of the object
 * but the callback function is not executed for the object itself.
 *
 * @param obj - The object whose properties will be iterated.
 * @param callback - A function to execute for each property. It receives the following parameters:
 *   - `key`: The key of the current property.
 *   - `value`: The value of the current property.
 *   - `path`: The dot-separated path to the current property.
 * @param path - The current path being traversed (used internally for recursion). Defaults to an empty string.
 */
export function iterateProperties(
  obj: Record<string, any>,
  callback: (key: string, value: any, path: string) => void,
  path = ''
) {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newPath = path ? path + '.' + key : key;

      // If the property is an object (and not null), recursively iterate its properties
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        iterateProperties(obj[key], callback, newPath);

        // Skip executing the callback for the current property if it is an object, an array or null/undefined
        continue;
      }

      // Execute the callback for the current property
      callback(key, obj[key], newPath);
    }
  }
}
